import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Home, MapPin, Users, LogIn } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const PropertiesTable = ({ 
  buildings = [], 
  isLoading = false, 
  onManageHospedajes 
}) => {
  const getTypeIcon = (tipo) => {
    const tipoStr = String(tipo || '').toLowerCase();
    switch (tipoStr) {
      case 'casa':
        return <Home className="h-4 w-4 text-blue-600" />;
      case 'apto':
      case 'apartamento':
        return <Building2 className="h-4 w-4 text-green-600" />;
      default:
        return <Building2 className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTypeLabel = (tipo) => {
    const tipoStr = String(tipo || '').toLowerCase();
    switch (tipoStr) {
      case 'casa':
        return 'Casa';
      case 'apto':
        return 'Apartamento';
      default:
        return tipo || 'Otro';
    }
  };

  const getStatusBadge = (estado) => {
    const variant = estado === 'Activo' ? 'default' : 'secondary';
    const className = estado === 'Activo' 
      ? 'bg-green-100 text-green-800 hover:bg-green-200' 
      : 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    
    return (
      <Badge variant={variant} className={`text-xs ${className}`}>
        {estado}
      </Badge>
    );
  };

  const handleManageHospedajes = (building) => {
    onManageHospedajes?.(building);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 rounded-t-lg"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-50 border-t border-gray-100"></div>
          ))}
        </div>
      </div>
    );
  }

  if (buildings.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center">
        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No se encontraron propiedades
        </h3>
        <p className="text-gray-500">
          Ajusta los filtros o verifica que existan propiedades en el sistema
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
          <div className="col-span-3">Propiedad</div>
          <div className="col-span-2">Tipo</div>
          <div className="col-span-2">Ciudad</div>
          <div className="col-span-3">Dirección</div>
          <div className="col-span-1 text-center">Hospedajes</div>
          <div className="col-span-1 text-center">Acciones</div>
        </div>
      </div>

      {/* Body */}
      <div className="divide-y divide-gray-200">
        <AnimatePresence>
          {buildings.map((building, index) => (
            <motion.div
              key={building._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2, delay: index * 0.02 }}
              className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* Propiedad */}
                <div className="col-span-3">
                  <div className="flex items-center space-x-3">
                    {getTypeIcon(building.tipo)}
                    <div>
                      <div className="font-medium text-gray-900">
                        {building.nombre}
                      </div>
                      <div className="flex items-center mt-1">
                        {getStatusBadge(building.estado)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tipo */}
                <div className="col-span-2">
                  <span className="text-sm text-gray-600">
                    {getTypeLabel(building.tipo)}
                  </span>
                </div>

                {/* Ciudad */}
                <div className="col-span-2">
                  <span className="text-sm text-gray-900 font-medium">
                    {building.ciudad}
                  </span>
                </div>

                {/* Dirección */}
                <div className="col-span-3">
                  <div className="flex items-center space-x-1 text-sm text-gray-600">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate" title={building.direccion}>
                      {building.direccion}
                    </span>
                  </div>
                </div>

                {/* Hospedajes */}
                <div className="col-span-1 text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-900">
                      {building.numeroHospedajes || 0}
                    </span>
                  </div>
                </div>

                {/* Acciones */}
                <div className="col-span-1 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleManageHospedajes(building)}
                    className="h-8 w-8 p-0 hover:bg-primary hover:text-primary-foreground"
                    title="Gestionar"
                  >
                    <LogIn className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer info */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          Mostrando {buildings.length} propiedades
        </div>
      </div>
    </div>
  );
};

export default PropertiesTable;
